/* eslint-disable @typescript-eslint/naming-convention */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UserService, isOwnOrUnresolvedUser } from '@ups/user';
import { UpsApiHttpErrorResponseCamelCase } from '@ups/common';
import { EventBusService } from '@ups/xplat/core';
import {
  DynamicItemBaseComponent,
  IFormReview,
  dynamicSaveIncomplete,
} from '@ups/xplat/dynamic-render';
import { MessageHelper } from '@ups/xplat/web/core';
import { filter, takeUntil } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
import { SecurityService } from '@ups/security';
import { SecurityConstants } from '@ups/security-constants';

@Component({
  selector: 'ups-dynamic-review',
  styleUrls: ['dynamic-review.component.scss'],
  templateUrl: 'dynamic-review.component.html',
  // WI 35909 (S2): created at runtime by dynamicItem under ups-dynamic-container; each async
  // write the template reads is marked below.
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DynamicReviewComponent
  extends DynamicItemBaseComponent
  implements OnInit, AfterViewInit
{
  @ViewChild('approvalResult') approvalResult: ElementRef;
  @ViewChild('reviewNotes') reviewNotes: ElementRef;

  showCancelConfirm = false;
  showDeleteConfirm = false;
  selectedButton = '';
  formReview: IFormReview;
  comment = '';
  approvalComments = '';
  reviewedBy = '';
  dateReviewed = '';
  isSaving = false;

  initialState = {
    selectedButton: null,
    comments: null,
  };

  dynamicComment = {
    isCommenting: false,
    commentText: '',
  };

  isReadOnly = false;
  isSubmittedByLoggedInUser = false;

  canReviewForms = false;
  canSelfReviewForms = false;
  canEditForms = false;
  canEditApprovedForms = false;
  canEditRejectedForms = false;

  canViewApprovalComments = false;
  canReadAllApprovalComments = false;
  canReadSelfApprovalComments = false;
  canEditAllApprovalComments = false;

  security = inject(SecurityService);
  userService = inject(UserService);
  private msg = inject(MessageHelper);
  private _eventBus = inject(EventBusService);

  constructor() {
    super();

    this.canReviewForms = this.security.getFeatureById(
      SecurityConstants.safety_2_0_forms_canreviewforms
    ).editAll;
    this.canSelfReviewForms = this.security.getFeatureById(
      SecurityConstants.safety_2_0_forms_canreviewforms
    ).editSelf;
    this.canEditForms = this.security.getFeatureById(
      SecurityConstants.safety_2_0_forms_caneditforms
    ).editAll;
    this.canEditApprovedForms = this.security.getFeatureById(
      SecurityConstants.safety_2_0_forms_caneditacceptedforms
    ).editAll;
    this.canEditRejectedForms = this.security.getFeatureById(
      SecurityConstants.safety_2_0_forms_caneditrejectedforms
    ).editAll;

    const fSecurity = this.security.getFeatureById(
      SecurityConstants.safety_2_0_forms_approvalcomments
    );
    this.canViewApprovalComments = fSecurity.viewFeature;
    this.canReadAllApprovalComments = fSecurity.readAll;
    this.canReadSelfApprovalComments = fSecurity.readSelf;
    this.canEditAllApprovalComments = fSecurity.editAll;
  }

  ngOnInit() {
    // overrides the base ngOnInit without super, so wire the base's in-place config-write mark
    this.markOnInPlaceConfigWrites();
    this.isReadOnly = this.config?.options?.isReadOnly;
    // WI 30201 shape C: fail closed. An unresolved profile is treated as own, so `isEditable()`
    // (gated on `!isSubmittedByLoggedInUser || canSelfReviewForms`) denies reviewing the user's
    // own submission instead of granting it when `myInfo?.UserId` is undefined.
    this.isSubmittedByLoggedInUser = isOwnOrUnresolvedUser(
      this.config.options.submittedUserId,
      this.userService.myInfo?.UserId
    );

    if (this.config.options.dynamicContainerResponseId) {
      this.dynamicService
        .getFormReview(this.config.options.dynamicContainerResponseId, 'Review')
        .toPromise()
        .then((d) => {
          this.cdr?.markForCheck();
          this.formReview = d;
          this.comment = d.Response;
          this.approvalComments = d.ApprovalComments;
          this.reviewedBy = d.ReviewedBy;
          this.dateReviewed = d.ReviewDate;
          this.selectedButton = d.FormReviewStatusName.toLowerCase();

          this.initialState.comments = d.Response;
          this.initialState.selectedButton =
            d.FormReviewStatusName.toLowerCase();
          this.selectReviewStatus(d.FormReviewStatusName.toLowerCase());
        });
    }
  }

  ngAfterViewInit(): void {
    this._eventBus
      .observe(this._eventBus.types.cmpReadyEvent)
      .pipe(
        filter((data) => data['type'] === 'dynamic-comments-component'),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {
        this.dynamicComment.isCommenting = data['instance'].isCommenting;
        this.dynamicComment.commentText = data['instance'].comment;
      });

    merge(
      fromEvent(this.approvalResult.nativeElement, 'click'),
      fromEvent(this.reviewNotes.nativeElement, 'input')
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        console.log('changes detected');

        this._eventBus.emit(this._eventBus.types.cmpReadyEvent, {
          type: 'dynamic-review-component',
          instance: this.checkForFormChanges.bind(this),
        });
      });
  }

  isApproved() {
    return (
      this.formReview && this.formReview.FormReviewStatusName === 'Approved'
    );
  }

  isRejected() {
    return (
      this.formReview && this.formReview.FormReviewStatusName === 'Rejected'
    );
  }

  isSubmitted() {
    return this.isApproved() || this.isRejected();
  }

  isEditingRejected() {
    return !this.isReadOnly && this.canEditRejectedForms && this.isRejected();
  }

  isEditable() {
    return (
      this.canReviewForms &&
      !this.isReadOnly &&
      (!this.isSubmittedByLoggedInUser || this.canSelfReviewForms) &&
      (!this.isSubmitted() || (this.canEditApprovedForms && this.isApproved()))
    );
  }

  isSubmitReview() {
    return (
      !this.isReadOnly &&
      !this.isSubmitted() &&
      (this.selectedButton === 'approved' || this.selectedButton === 'rejected')
    );
  }

  canAddApprovalComments() {
    return (
      !this.formReview && this.canEditAllApprovalComments && this.isEditable()
    );
  }

  canReadApprovalComments() {
    // WI 30201 shape C: `myInfo` is null in the failure arms, so the bare read threw here.
    // `?.` makes the self-read fail closed (unknown identity cannot match a real ReviewedUserId).
    const isReviewedByLoggedInUser =
      this.formReview?.ReviewedUserId === this.userService.myInfo?.UserId;
    return (
      this.formReview &&
      (this.canReadAllApprovalComments ||
        (isReviewedByLoggedInUser &&
          (this.canReadSelfApprovalComments ||
            this.canEditAllApprovalComments)))
    );
  }

  canEditApprovalComments() {
    return (
      this.canReadApprovalComments() &&
      this.canEditAllApprovalComments &&
      this.isEditable()
    );
  }

  selectReviewStatus(status: string) {
    this.selectedButton = status;
  }

  showApprovalComments() {
    return (
      this.selectedButton === 'approved' &&
      (this.canAddApprovalComments() ||
        this.canReadApprovalComments() ||
        this.canEditApprovalComments())
    );
  }

  isValid() {
    return (
      this.config.options.dynamicContainerId &&
      this.config.options.dynamicContainerResponseId &&
      this.selectedButton &&
      (this.selectedButton === 'approved' ||
        this.selectedButton === 'inreview' ||
        (this.selectedButton === 'rejected' &&
          this.comment &&
          this.comment.length))
    );
  }

  closeTab() {
    this.showCancelConfirm = false;
    this.dynamicService.formReviewDone$.next();
  }

  checkForFormChanges() {
    return (
      this.initialState.selectedButton !== this.selectedButton ||
      this.initialState.comments !== this.comment ||
      !!this.comment.length
    );
  }

  resetFormChanges() {
    this.initialState.selectedButton = this.selectedButton;
    this.initialState.comments = this.comment;

    this._eventBus.emit(this._eventBus.types.cmpReadyEvent, {
      type: 'dynamic-review-component',
      instance: this.checkForFormChanges.bind(this),
    });
  }

  submitReview() {
    if (this.dynamicComment.isCommenting && this.dynamicComment.commentText) {
      const msg =
        'There is an unsaved comment. Are you sure you want to continue?';

      this.win.confirm(msg).then((ok) => {
        if (ok) {
          this.submit();
        }
      });
    } else {
      this.submit();
    }
  }

  submit() {
    const submit = () => {
      const dto = {
        DynamicContainerId: this.config.options.dynamicContainerId,
        DynamicContainerResponseId:
          this.config.options.dynamicContainerResponseId,
        FormReviewStatusName: this.selectedButton,
        Response: this.comment,
        ApprovalComments: this.approvalComments,
        ReviewType: 'Review',
      } as IFormReview;

      if (this.config.options.reviewId)
        dto.FormReviewId = this.config.options.reviewId;

      this.isSaving = true;
      // submit runs from activeAction()/confirm .then callbacks, outside any template event.
      this.cdr?.markForCheck();
      this.dynamicService
        .saveFormReview(dto)
        .toPromise()
        .then(
          (d: IFormReview) => {
            this.cdr?.markForCheck();
            if (d.FormReviewStatusName === 'inreview') {
              this.msg.success('Saved Successfully');
            }
            this.isSaving = false;
            this.resetFormChanges();
            this.closeTab();
          },
          (e: UpsApiHttpErrorResponseCamelCase) => {
            this.cdr?.markForCheck();
            this.isSaving = false;
            this.msg.error(e?.error.message);
          }
        );
    };

    if (this.config.options.isEditReview) {
      this.dynamicService.activeAction().then((res) => {
        // #34974 + WI 34982: never submit the review over a failed or partly refused save
        if (!dynamicSaveIncomplete(res)) submit();
      });
    } else {
      submit();
    }
  }
}
